import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, XMarkIcon } from 'components/icons';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { subscriptionApi } from '../services/api';

// Self-serve checkout is off during the comped beta. Stripe is still on test
// keys and has no price matching the published Standard/Pro figures, so a
// purchase here would take a card in test mode and charge the wrong amount.
// Beta plans are granted manually. Flip to false once live prices exist.
const COMPED_BETA = true;

const plans = [
  {
    name: 'Free',
    description: 'Explore opportunities and see what Clevor can do',
    price: { monthly: 0, annually: 0 },
    features: [
      { name: 'Opportunity search (15 results, first 3 pages)', included: true },
      { name: 'SAM.gov integration', included: true },
      { name: 'Watch list', included: true },
      { name: 'Match scoring', included: true },
      { name: 'Single user', included: true },
      { name: 'AI Consultant', included: false },
      { name: 'Pipeline & CRM', included: false },
      { name: 'AI Proposal Writer', included: false },
    ],
    cta: 'Get Started Free',
    upgradeCta: 'Current Plan',
    planId: 'FREE',
    mostPopular: false,
  },
  {
    name: 'Standard',
    description: 'For contractors ready to use AI on real opportunities',
    price: { monthly: 124, annually: 99 },
    features: [
      { name: 'Full opportunity search', included: true },
      { name: 'Pipeline management', included: true },
      { name: 'AI Document Hub', included: true },
      { name: 'AI Consultant (reads your docs)', included: true },
      { name: 'Match scoring', included: true },
      { name: '200 AI credits/month', included: true },
      { name: 'Single user', included: true },
      { name: 'AI Proposal Writer', included: false },
      { name: 'AI Actions (6 agents)', included: false },
      { name: 'CRM & Contacts', included: false },
    ],
    cta: 'Start Free Trial',
    upgradeCta: 'Upgrade to Standard',
    planId: 'STANDARD',
    mostPopular: false,
  },
  {
    name: 'Pro',
    description: 'For growing GovCon teams',
    price: { monthly: 379, annually: 299 },
    features: [
      { name: 'Every federal solicitation on SAM.gov, refreshed hourly', included: true },
      { name: 'Watch criteria with a daily match digest', included: true },
      { name: 'Market intelligence', included: true },
      { name: 'GovCon AI Chat', included: true },
      { name: 'AI Document Hub', included: true },
      { name: 'AI proposal writer', included: true },
      { name: 'GovCon CRM', included: true },
      { name: 'Up to 10 team members', included: true },
      { name: 'SSO/SAML', included: false },
    ],
    cta: 'Start Free Trial',
    upgradeCta: 'Upgrade to Pro',
    planId: 'PRO',
    mostPopular: true,
  },
  {
    name: 'Enterprise',
    description: 'For large firms with compliance needs',
    price: { monthly: 'Custom', annually: 'Custom' },
    features: [
      { name: 'Everything in Pro', included: true },
      { name: 'SSO (SAML) + SCIM', included: true },
      { name: 'Audit logs & access controls', included: true },
      { name: 'Enterprise team workspaces', included: true },
      { name: 'Advanced role permissions', included: true },
      { name: 'Custom integrations', included: true },
      { name: 'Cloud/VPC/On-prem options', included: true },
      { name: 'Dedicated account manager', included: true },
      { name: 'Implementation & onboarding', included: true },
      { name: '< 1 Hour SLA support', included: true },
      { name: 'Priority feature requests', included: true },
    ],
    cta: 'Contact Sales',
    upgradeCta: 'Contact Sales',
    planId: 'ENTERPRISE',
    mostPopular: false,
  },
];

const Pricing: React.FC = () => {
  const [annual, setAnnual] = React.useState(true);
  const [upgrading, setUpgrading] = React.useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const isFreePlan = user?.plan === 'FREE';
  const isLoggedIn = isAuthenticated;

  const handlePlanClick = async (plan: typeof plans[0]) => {
    // Enterprise always goes to contact
    if (plan.name === 'Enterprise') {
      window.location.href = 'mailto:sales@clevor.ai?subject=Enterprise%20Plan%20Inquiry';
      return;
    }

    // During the comped beta, paid tiers route to sales rather than checkout.
    if (COMPED_BETA && plan.planId !== 'FREE') {
      window.location.href =
        `mailto:sales@clevor.ai?subject=${encodeURIComponent(`${plan.name} plan - beta access`)}`;
      return;
    }

    // Not logged in - go to signup
    if (!isLoggedIn) {
      navigate('/signup');
      return;
    }

    // Already on a paid plan - go to billing
    if (!isFreePlan) {
      navigate('/billing');
      return;
    }

    // Free user upgrading - create checkout session
    try {
      setUpgrading(plan.name);
      const response = await subscriptionApi.createCheckoutSession({
        plan: plan.planId,
        isAnnual: annual,
      });
      
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Failed to create checkout:', error);
      toast.error('We could not start checkout. Please try again, or email support@clevor.ai.');
    } finally {
      setUpgrading(null);
    }
  };

  return (
    <div className="bg-surface py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-overline uppercase text-brand-600">Pricing</h2>
          <p className="mt-2 text-display text-content sm:text-display-lg text-balance">
            Simple, transparent pricing
          </p>
          <p className="mt-6 text-lg leading-8 text-content-muted">
            Start free and upgrade when you're ready. No credit card required.
          </p>
          
          {/* Current plan indicator for logged in users */}
          {isLoggedIn && (
            <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-brand-50 text-brand-700 text-sm font-medium">
              Your current plan: <span className="ml-1 font-bold">{user?.plan}</span>
            </div>
          )}
        </div>

        {/* Toggle */}
        <div className="mt-10 flex justify-center">
          <div className="flex items-center space-x-4 bg-surface-sunk p-1 rounded-lg">
            <button
              onClick={() => setAnnual(false)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                !annual ? 'bg-surface text-content shadow' : 'text-content-muted'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                annual ? 'bg-surface text-content shadow' : 'text-content-muted'
              }`}
            >
              Annual <span className="text-green-600 text-xs">(Save 20%)</span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-y-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-4 lg:gap-x-6">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              className={`flex flex-col justify-between rounded-3xl bg-surface p-8 ring-1 ring-border xl:p-10 ${
                plan.mostPopular ? 'ring-2 ring-brand-600' : ''
              }`}
            >
              <div>
                <div className="flex items-center justify-between gap-x-4">
                  <h3 className="text-lg font-semibold leading-8 text-content">{plan.name}</h3>
                  {plan.mostPopular && (
                    <p className="rounded-full bg-brand-600/10 px-2.5 py-1 text-xs font-semibold leading-5 text-brand-600">
                      Most popular
                    </p>
                  )}
                </div>
                <p className="mt-4 text-sm leading-6 text-content-muted">{plan.description}</p>
                <p className="mt-6 flex items-baseline gap-x-1">
                  <span className="text-display text-content tabular-nums">
                    {plan.price[annual ? 'annually' : 'monthly'] === 0
                      ? 'Free'
                      : typeof plan.price[annual ? 'annually' : 'monthly'] === 'number'
                        ? `$${plan.price[annual ? 'annually' : 'monthly']}`
                        : plan.price[annual ? 'annually' : 'monthly']}
                  </span>
                  {typeof plan.price[annual ? 'annually' : 'monthly'] === 'number' && plan.price[annual ? 'annually' : 'monthly'] !== 0 && (
                    <span className="text-sm font-semibold leading-6 text-content-muted">/month</span>
                  )}
                </p>
                {typeof plan.price.annually === 'number' && plan.price.annually !== 0 && (
                  <p className="mt-1 text-xs text-content-muted">
                    {annual
                      ? `Billed annually at $${(plan.price.annually as number) * 12}/year`
                      : 'Billed monthly, cancel anytime'}
                  </p>
                )}
                <ul className="mt-8 space-y-3 text-sm leading-6 text-content-muted">
                  {plan.features.map((feature) => (
                    <li key={feature.name} className="flex gap-x-3">
                      {feature.included ? (
                        <CheckIcon className="h-6 w-5 flex-none text-brand-600" aria-hidden="true" />
                      ) : (
                        <XMarkIcon className="h-6 w-5 flex-none text-content-subtle" aria-hidden="true" />
                      )}
                      {feature.name}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => handlePlanClick(plan)}
                disabled={upgrading === plan.name}
                className={`mt-8 block w-full rounded-md px-3 py-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  plan.mostPopular
                    ? 'bg-brand-600 text-white shadow-sm hover:bg-brand-500 focus-visible:outline-brand-600'
                    : 'bg-brand-50 text-brand-700 hover:bg-brand-100'
                }`}
              >
                {upgrading === plan.name ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </span>
                ) : COMPED_BETA && plan.planId !== 'FREE' && plan.name !== 'Enterprise' ? (
                  'Request beta access'
                ) : isLoggedIn ? (
                  isFreePlan ? plan.upgradeCta : (user?.plan === plan.planId ? 'Current Plan' : 'Switch Plan')
                ) : (
                  plan.cta
                )}
              </button>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mx-auto max-w-4xl mt-24">
          <h2 className="text-title-lg text-content text-center mb-8">Frequently Asked Questions</h2>
          <dl className="space-y-8">
            {[
              {
                q: 'Can I try before I buy?',
                a: 'The Free plan is free, no card. The paid plans are in private beta: request access and, while the beta runs, we set you up at no charge.',
              },
              {
                q: 'What happens when the beta ends?',
                a: 'You choose a plan then. Nothing is charged until you enter payment details yourself; if you do not subscribe, your account continues on the Free plan.',
              },
              {
                q: 'Can I upgrade or downgrade my plan?',
                a: 'Yes, you can change your plan at any time. When upgrading, you\'ll be charged a prorated amount. When downgrading, the new rate takes effect at the next billing cycle.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards (Visa, MasterCard, American Express) and ACH bank transfers for annual Enterprise plans.',
              },
              {
                q: 'Is there a setup fee?',
                a: 'No setup fees on Free, Standard, or Pro. Enterprise plans may include implementation fees based on your requirements.',
              },
              {
                q: 'How does annual billing work?',
                a: 'Annual plans show the equivalent monthly rate and are billed once up front for twelve months. Monthly plans are billed every month and can be cancelled at any time.',
              },
            ].map((faq, i) => (
              <div key={i}>
                <dt className="text-title text-content">{faq.q}</dt>
                <dd className="mt-2 text-content-muted">{faq.a}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* CTA */}
        <div className="mt-24 text-center">
          <h2 className="text-title-lg text-content">Still have questions?</h2>
          <p className="mt-4 text-content-muted">
            Contact our sales team for a personalized demo or to discuss custom Enterprise requirements.
          </p>
          <a
            href="mailto:sales@clevor.ai"
            className="mt-6 inline-block bg-brand-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-brand-500 transition"
          >
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
